import { z } from "zod";
import { normalizeBlockPropsForRender } from "../block-registry.js";

const SECTION_COMPONENT_TYPES = {
  hero: new Set(["hero-section"]),
  testimonials: new Set(["testimonial-grid"]),
  services: new Set(["feature-grid"]),
  booking: new Set(["booking-widget", "calendar-embed"]),
  contact: new Set(["lead-form", "multi-step-form"]),
} as const;

const PROOF_COMPONENT_TYPES = new Set([
  "testimonial-grid",
  "social-proof",
  "logo-cloud",
  "stats-counter",
  "social-proof-popup",
]);

export const sectionIntelligenceIssueSchema = z.object({
  sectionType: z.enum(["hero", "testimonials", "services", "booking", "contact"]),
  componentId: z.string().min(1).optional(),
  ruleId: z.string().min(1).max(120),
  severity: z.enum(["critical", "warning", "info"]),
  message: z.string().min(1).max(400),
});

export const sectionIntelligenceResultSchema = z.object({
  issues: z.array(sectionIntelligenceIssueSchema),
});

export type SectionIntelligenceIssue = z.infer<typeof sectionIntelligenceIssueSchema>;
export type SectionIntelligenceResult = z.infer<typeof sectionIntelligenceResultSchema>;

type PuckComponent = {
  type?: string;
  props?: Record<string, unknown>;
};

type PuckLikeData = {
  content?: PuckComponent[];
  zones?: Record<string, PuckComponent[]>;
};

function readString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function wordCount(value: string): number {
  return value.trim().split(/\s+/).filter(Boolean).length;
}

function collectComponents(puckData: PuckLikeData): PuckComponent[] {
  const content = Array.isArray(puckData.content) ? puckData.content : [];
  const zoneItems = puckData.zones && typeof puckData.zones === "object"
    ? Object.values(puckData.zones).flatMap((items) => (Array.isArray(items) ? items : []))
    : [];

  return [...content, ...zoneItems];
}

function componentIdFromProps(props: Record<string, unknown>): string | undefined {
  const id = readString(props.id);
  return id || undefined;
}

function buildIssue(
  sectionType: SectionIntelligenceIssue["sectionType"],
  ruleId: string,
  severity: SectionIntelligenceIssue["severity"],
  message: string,
  componentId?: string,
): SectionIntelligenceIssue {
  return {
    sectionType,
    componentId,
    ruleId,
    severity,
    message,
  };
}

function validateHeroSection(components: PuckComponent[]): SectionIntelligenceIssue[] {
  const hero = components.find((component) => component.type && SECTION_COMPONENT_TYPES.hero.has(component.type));
  if (!hero?.props || !hero.type) {
    return [];
  }

  const props = normalizeBlockPropsForRender(hero.type, hero.props);
  const componentId = componentIdFromProps(props);
  const headline = readString(props.headline);
  const subheadline = readString(props.subheadline);
  const ctaText = readString(props.ctaText);
  const hasProof = components.some((component) => component.type && PROOF_COMPONENT_TYPES.has(component.type));

  const issues: SectionIntelligenceIssue[] = [];
  if (wordCount(headline) > 12) {
    issues.push(buildIssue("hero", "hero-benefit-headline", "warning", "Hero headline is too long for a 3-second scan. Keep it tighter and outcome-led.", componentId));
  }
  if (!subheadline) {
    issues.push(buildIssue("hero", "hero-subheadline", "warning", "Hero is missing a clarifying subheadline.", componentId));
  }
  if (!ctaText) {
    issues.push(buildIssue("hero", "hero-cta", "critical", "Hero needs a visible primary CTA.", componentId));
  }
  if (!hasProof) {
    issues.push(buildIssue("hero", "hero-proof", "info", "Page has no trust/proof section near the hero yet.", componentId));
  }

  return issues;
}

function validateTestimonialsSection(components: PuckComponent[]): SectionIntelligenceIssue[] {
  const block = components.find((component) => component.type && SECTION_COMPONENT_TYPES.testimonials.has(component.type));
  if (!block?.props || !block.type) {
    return [];
  }

  const props = normalizeBlockPropsForRender(block.type, block.props);
  const componentId = componentIdFromProps(props);
  const testimonials = Array.isArray(props.testimonials) ? props.testimonials as Array<Record<string, unknown>> : [];
  const issues: SectionIntelligenceIssue[] = [];

  if (testimonials.length < 3) {
    issues.push(buildIssue("testimonials", "testimonials-volume", "info", "Testimonials section has fewer than three proof items. Add more real reviews when available.", componentId));
  }

  if (testimonials.some((item) => wordCount(readString(item.quote)) < 8)) {
    issues.push(buildIssue("testimonials", "testimonials-specificity", "warning", "At least one testimonial lacks specific detail. Prefer concrete outcomes over generic praise.", componentId));
  }

  if (testimonials.some((item) => !readString(item.name))) {
    issues.push(buildIssue("testimonials", "testimonials-identity", "warning", "At least one testimonial is missing a real author name.", componentId));
  }

  return issues;
}

function validateServicesSection(components: PuckComponent[]): SectionIntelligenceIssue[] {
  const block = components.find((component) => component.type && SECTION_COMPONENT_TYPES.services.has(component.type));
  if (!block?.props || !block.type) {
    return [];
  }

  const props = normalizeBlockPropsForRender(block.type, block.props);
  const componentId = componentIdFromProps(props);
  const features = Array.isArray(props.features) ? props.features as Array<Record<string, unknown>> : [];
  const issues: SectionIntelligenceIssue[] = [];

  if (features.some((item) => !readString(item.title))) {
    issues.push(buildIssue("services", "services-title", "critical", "Every service card needs a clear service name.", componentId));
  }

  if (features.some((item) => wordCount(readString(item.description)) < 6)) {
    issues.push(buildIssue("services", "services-description", "warning", "Services need short outcome-focused descriptions, not names alone.", componentId));
  }

  return issues;
}

function validateBookingSection(components: PuckComponent[]): SectionIntelligenceIssue[] {
  const block = components.find((component) => component.type && SECTION_COMPONENT_TYPES.booking.has(component.type));
  if (!block?.props || !block.type) {
    return [];
  }

  const props = normalizeBlockPropsForRender(block.type, block.props);
  const componentId = componentIdFromProps(props);
  const serviceId = readString(props.serviceId || props.calendarId);
  const bookingUrl = readString(props.bookingUrl);
  const issues: SectionIntelligenceIssue[] = [];

  if (!serviceId && !bookingUrl) {
    issues.push(buildIssue("booking", "booking-live-availability", "critical", "Booking section is missing both a live service binding and a fallback booking URL.", componentId));
  } else if (!serviceId && bookingUrl) {
    issues.push(buildIssue("booking", "booking-connect-prompt", "info", "Booking section is using a fallback URL only. Connect a real service for live availability.", componentId));
  }

  return issues;
}

function parseLeadFields(props: Record<string, unknown>): Array<Record<string, unknown>> {
  const fields = props.fields;
  if (!Array.isArray(fields)) {
    return [];
  }

  return fields.filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === "object");
}

function validateContactSection(components: PuckComponent[]): SectionIntelligenceIssue[] {
  const block = components.find((component) => component.type && SECTION_COMPONENT_TYPES.contact.has(component.type));
  if (!block?.props || !block.type) {
    return [];
  }

  const props = normalizeBlockPropsForRender(block.type, block.props);
  const componentId = componentIdFromProps(props);
  const fields = parseLeadFields(props);
  const fieldNames = fields.map((field) => readString(field.name).toLowerCase());
  const issues: SectionIntelligenceIssue[] = [];

  if (!fieldNames.some((name) => name.includes("email"))) {
    issues.push(buildIssue("contact", "contact-email", "critical", "Contact form needs an email field for CRM follow-up.", componentId));
  }
  if (!readString(props.consentText)) {
    issues.push(buildIssue("contact", "contact-consent", "warning", "Contact form is missing GDPR consent copy.", componentId));
  }
  if (fields.length > 5) {
    issues.push(buildIssue("contact", "contact-friction", "info", "Contact form has more than five fields. Trim it for better conversion.", componentId));
  }

  return issues;
}

export function buildSectionIntelligencePrompt(): string {
  return [
    "## Supporting Layer: Section Intelligence Templates",
    "Apply these section-specific generation and review rules when the page includes the matching section type.",
    "Hero: write an outcome-led headline, a clarifying subheadline, one visible CTA, and pair it with nearby trust proof.",
    "Testimonials: prefer the best real reviews, require a real author, and keep quotes specific instead of generic praise.",
    "Services: turn service names into short concrete descriptions and avoid title-only cards.",
    "Booking: prefer live service availability; if that is unavailable, generate a connect-calendar or fallback booking prompt instead of fake slots.",
    "Contact: keep the form low-friction, include email capture, and include consent language for CRM follow-up.",
  ].join("\n");
}

export function validateSectionIntelligence(puckData: PuckLikeData): SectionIntelligenceResult {
  const components = collectComponents(puckData);
  const issues = [
    ...validateHeroSection(components),
    ...validateTestimonialsSection(components),
    ...validateServicesSection(components),
    ...validateBookingSection(components),
    ...validateContactSection(components),
  ];

  return sectionIntelligenceResultSchema.parse({ issues });
}
